import { useCallback, useEffect, useState } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { LS_KEY_LOGIN_TOKEN } from 'src/constants/localStorageKeys';
import { isAdminMode } from 'src/helpers/storedToken';
import { arenaToast } from 'src/components/arenaSnackbar';
import showRememberForeverHint from 'src/components/pushNotificationPrompt/rememberForeverHint';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { updateApplicantMetadata } from 'src/redux/slices/candidateLayout';
import {
  useDismissWebPushPromptMutation,
  useRemoveWebPushSubscriptionMutation,
  useSaveWebPushSubscriptionMutation,
} from 'src/redux/services/webPush';
import {
  dropSignedOutPushSubscription,
  ensurePushSubscription,
  getExistingPushSubscription,
  getPushPermission,
  isChromiumPrompt,
  isPushOptedOut,
  isWebPushSupported,
  rememberPushEndpoint,
  setPushOptedOut,
  takeRememberedPushEndpoint,
  toSubscribeBody,
} from 'src/helpers/webPush';

export const PUSH_BLOCKED_MESSAGE =
  "To get notifications, allow them for Abekus in your browser's site settings.";

/**
 * What went wrong, from the error `pushManager.subscribe` threw.
 *
 * NotAllowedError after a granted prompt is Chrome refusing push on an allow
 * that is only "until I close this site"; AbortError is the browser failing to
 * reach its push service (switched off in its settings, a VPN or firewall
 * blocking it, or a browser build without one).
 */
const subscribeErrorMessage = (err: unknown) => {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError') {
    return 'To get notifications, allow them "forever" for Abekus in your browser\'s site settings.';
  }
  if (name === 'AbortError') {
    // The browser couldn't reach its push service: switched off in its
    // settings (Brave's default), or blocked by the network or a VPN.
    return 'To get notifications, turn on push messaging in your browser settings and check your internet, then try again.';
  }
  return "Couldn't turn on notifications. Please try again.";
};

/**
 * Browser notifications for the signed-in user: whether they can be asked for,
 * and the calls that turn them on and off.
 *
 * `canPrompt` is the gate for the opt-in card. The server owns the part that
 * must hold across browsers (snoozed after a dismissal, gone after three, never
 * on a first visit); this adds what only the browser knows.
 */
const usePushSubscription = () => {
  const dispatch = useAppDispatch();
  const supported = isWebPushSupported();
  const userId = useAppSelector((state) => state.auth.user?.id);
  // Debug Mode is the admin's browser: subscribing, or saying "not now", would
  // act as the learner -- moving their invites to the admin's desktop.
  const adminMode = isAdminMode();
  // Rides on the applicant status payload the layout already loads.
  const status = useAppSelector(
    (state) => state.candidate.applicantInfoData?.web_push,
  );
  const [saveSubscription] = useSaveWebPushSubscriptionMutation();
  const [removeSubscription] = useRemoveWebPushSubscriptionMutation();
  const [dismissPrompt] = useDismissWebPushPromptMutation();

  const [permission, setPermission] = useState(getPushPermission);
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getExistingPushSubscription()
      .then((subscription) => setIsSubscribed(!!subscription))
      .catch(() => setIsSubscribed(false));
  }, [supported]);

  const vapidPublicKey = status?.vapid_public_key;

  const enable = useCallback(async () => {
    if (!vapidPublicKey || !userId || adminMode) return false;
    setIsBusy(true);
    try {
      // Only when a prompt will actually open: an allowed browser resolves
      // at once, and the tip would flash for nothing.
      const hint =
        isChromiumPrompt() && Notification.permission === 'default'
          ? showRememberForeverHint()
          : undefined;
      const result = await Notification.requestPermission().finally(() => {
        if (hint) closeSnackbar(hint);
      });
      setPermission(result);
      if (result === 'denied') {
        enqueueSnackbar(PUSH_BLOCKED_MESSAGE, { variant: 'info' });
        return false;
      }
      // 'default' means the prompt was closed without an answer. That is not a
      // "no" to us, so nothing is recorded: the card only steps aside for this
      // page. Chrome itself quietens a site whose prompts keep being closed.
      if (result !== 'granted') return false;
      const subscription = await ensurePushSubscription(vapidPublicKey);
      await saveSubscription(toSubscribeBody(subscription)).unwrap();
      rememberPushEndpoint(subscription.endpoint);
      setPushOptedOut(userId, false);
      setIsSubscribed(true);
      arenaToast(
        "You'll now get important updates, even when Abekus is closed.",
        { tone: 'success', label: 'Notifications are on' },
      );
      return true;
    } catch (err) {
      // Logged, not swallowed: subscribe failures are browser-side and leave no
      // trace on the server, so the console is the only place they show up.
      // eslint-disable-next-line no-console
      console.error('Web push subscribe failed', err);
      enqueueSnackbar(subscribeErrorMessage(err), { variant: 'error' });
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [vapidPublicKey, userId, adminMode, saveSubscription]);

  const disable = useCallback(async () => {
    setIsBusy(true);
    try {
      const subscription = await getExistingPushSubscription();
      takeRememberedPushEndpoint();
      if (userId) setPushOptedOut(userId, true);
      if (subscription) {
        await removeSubscription({ endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
    } finally {
      setIsBusy(false);
    }
  }, [userId, removeSubscription]);

  const isAvailable = supported && !adminMode && !!status?.is_configured;

  return {
    isAvailable,
    permission,
    isSubscribed: !!isSubscribed,
    isBusy,
    // Not asked yet, or allowed but not subscribed here (switched off in
    // Settings, or allowed under another account) -- both have something to
    // turn on. Blocked has nothing the card can do.
    canPrompt:
      isAvailable &&
      !!status?.can_prompt &&
      (permission === 'default' || permission === 'granted') &&
      isSubscribed === false,
    enable,
    disable,
    dismiss: () => {
      if (adminMode) return;
      dismissPrompt();
      // Hide it on every surface now, not only on the next status load.
      if (status) {
        dispatch(
          updateApplicantMetadata({
            web_push: { ...status, can_prompt: false },
          }),
        );
      }
    },
  };
};

/**
 * Keeps the server's view of this browser in step with the browser.
 *
 * Granted: re-send the subscription on every login — browsers rotate them
 * without asking, another account may have signed in on this machine, and the
 * server drops endpoints that stop working. It never prompts.
 *
 * Not granted but an endpoint was registered here: the allow lapsed ("until I
 * close this site") or was revoked in site settings. The browser has already
 * dropped the subscription, so the remembered endpoint is the only way to take
 * it off the server before it is counted as reachable.
 */
export const usePushSubscriptionSync = () => {
  const { isLoginSuccess, user } = useAppSelector((state) => state.auth);
  const userId = user?.id;
  const supported = isWebPushSupported();
  // Signed in as the learner, but the browser and its endpoint are the admin's.
  const adminMode = isAdminMode();
  const granted = supported && Notification.permission === 'granted';
  const status = useAppSelector(
    (state) => state.candidate.applicantInfoData?.web_push,
  );
  const [saveSubscription] = useSaveWebPushSubscriptionMutation();
  const [removeSubscription] = useRemoveWebPushSubscriptionMutation();
  const vapidPublicKey = status?.vapid_public_key;

  // The token, not isLoginSuccess: that starts false on every load, before
  // the session has been restored.
  useEffect(() => {
    if (!supported || localStorage.getItem(LS_KEY_LOGIN_TOKEN)) return;
    dropSignedOutPushSubscription();
  }, [supported, isLoginSuccess]);

  useEffect(() => {
    if (!supported || granted || !isLoginSuccess || adminMode) return;
    const lapsed = takeRememberedPushEndpoint();
    if (lapsed) removeSubscription({ endpoint: lapsed });
  }, [supported, granted, isLoginSuccess, adminMode, removeSubscription]);

  useEffect(() => {
    // Switched off in Settings here: the permission is still granted, but
    // the answer was no.
    if (
      !granted ||
      !isLoginSuccess ||
      !userId ||
      adminMode ||
      !vapidPublicKey ||
      isPushOptedOut(userId)
    ) {
      return;
    }
    ensurePushSubscription(vapidPublicKey)
      .then(async (subscription) => {
        await saveSubscription(toSubscribeBody(subscription)).unwrap();
        rememberPushEndpoint(subscription.endpoint);
      })
      .catch(() => {});
  }, [
    granted,
    isLoginSuccess,
    userId,
    adminMode,
    vapidPublicKey,
    saveSubscription,
  ]);
};

/**
 * What an opt-in card needs, whatever it looks like: when to show, and the two
 * answers. Kept apart from the markup so each surface can dress the ask in its
 * own design system without re-deriving the rules.
 */
export const usePushPromptCard = () => {
  const { canPrompt, isBusy, enable, dismiss } = usePushSubscription();
  const [hidden, setHidden] = useState(false);

  return {
    visible: canPrompt && !hidden,
    isBusy,
    turnOn: async () => {
      await enable();
      // Whatever the answer, the browser has now asked; the card has nothing
      // left to offer on this page.
      setHidden(true);
    },
    notNow: () => {
      setHidden(true);
      dismiss();
    },
  };
};

export default usePushSubscription;
